import { useController, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'ui/mobile-ds';
import FullPageModal from 'ui/ds/FullPageModal';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useRelationContext } from '@/context/RelationContext';
import useBreakpoints from '@/hooks/useBreakpoints';
import { EMemberRelationship, TAppdate } from '@/types';
import { format, isAfter, isBefore } from 'date-fns';
import DatePicker from '../DatePicker/DatePicker';
import {
  SelectMenu,
  SelectMenuButton,
  SelectMenuLabel,
  SelectMenuOption,
  SelectMenuOptions,
  SelectWrapper,
} from 'ui/ds/SelectMenu';
import { ArrowDown, InfoIcon } from 'ui/icons';
import useWindowDimensions from '@/hooks/useWindowDimensions';
import { calculateDaysFromToday } from '@/utils/calculateDaysFromToday';
import FormInput from '../FormInput/FormInput';
import { Exclaimation } from 'ui/retail-ds/icons';
import { twConfig } from 'ui/ds/utils/getConfig';
import { get } from 'lodash';
import Checkbox from '@/components/shared/Checkbox';
import toast from 'react-hot-toast';

interface IProps {
  isOpen: boolean;
  onOpenChange: (open?: boolean) => void;
  relation: string;
  backDateForDOB: TAppdate;
  backDateForMarriageDate: TAppdate;
  maximumAge: TAppdate;
  minimumAge: TAppdate;
}

const schema = (
  relation: string,
  dobDate: Date,
  marriageDate: Date,
  maxAgeDate: Date,
  minAgeDate: Date,
) =>
  z.object({
    selfDeclaration: z.boolean(),
    firstName: z
      .string()
      .min(1, 'First name is required')
      .regex(
        /^[A-Za-z]+$/,
        'First name must contain only alphabets (no spaces or numbers)',
      ),
    lastName: z.string().optional(),
    relationship: z.string().min(1, 'Relationship is required'),
    gender: z.string().min(1, 'Gender is required'),
    dateOfBirth: z
      .string()
      .min(1, 'Date of birth is required')
      .refine(
        (value) => {
          return value.trim().length > 0;
        },
        {
          message: 'Date of birth is required.',
        },
      )
      .refine(
        (value) => {
          if (relation === EMemberRelationship.SPOUSE && value) {
            return (
              isAfter(value, maxAgeDate) && // Validate against maximum age
              isBefore(value, minAgeDate)
            );
          }
          return isAfter(value, dobDate) && isBefore(value, new Date());
        },
        EMemberRelationship.SPOUSE === relation && maxAgeDate && minAgeDate
          ? `Date of birth must be after ${format(
              maxAgeDate,
              'dd-MMM-yyyy',
            )} and before ${format(minAgeDate, 'dd-MMM-yyyy')}`
          : `Date of birth must be after ${format(
              dobDate,
              'dd-MMM-yyyy',
            )} and before today.`,
      ),

    dateOfMarriage:
      relation === EMemberRelationship.SPOUSE
        ? z
            .string()
            .min(1, 'Date of marriage is required') // Make required for SPOUSE
            .refine(
              (value) => {
                return (
                  isAfter(value, marriageDate) && isBefore(value, new Date())
                );
              },
              `Date of marriage must be after ${format(marriageDate, 'dd-MMM-yyyy')} and before today.`,
            )
        : z.string().optional(), // Optional for other relations
  });

type FormData = z.infer<ReturnType<typeof schema>>;

const CHILD_DECLARATION_TEXT =
  'I confirm that the date of birth provided above is accurate. I understand that this information cannot be changed later, and any discrepancies identified during an audit may lead to claim denial';

const SPOUSE_DECLARATION_TEXT =
  'I confirm that the date of marriage provided above is accurate. I understand that this information cannot be changed later, and any discrepancies identified during an audit may lead to claim denial';

const AddMemberForm = ({
  isOpen,
  onOpenChange,
  relation,
  backDateForDOB,
  backDateForMarriageDate,
  minimumAge,
  maximumAge,
}: IProps) => {
  const [[viewportWidth]] = useWindowDimensions();
  // Convert backDateForDOB to a Date object
  const dobDate = backDateForDOB
    ? new Date(
        backDateForDOB.year,
        backDateForDOB.monthNumber - 1,
        backDateForDOB.date,
      )
    : new Date(); // Adjust month for zero-based index

  // Convert backDateForMarriageDate to a Date object
  const marriageDate = backDateForMarriageDate
    ? new Date(
        backDateForMarriageDate.year,
        backDateForMarriageDate.monthNumber - 1,
        backDateForMarriageDate.date,
      )
    : new Date(); // Adjust month for zero-based index

  const minAgeDate = minimumAge
    ? new Date(minimumAge.year, minimumAge.monthNumber - 1, minimumAge.date)
    : new Date();

  const maxAgeDate = maximumAge
    ? new Date(maximumAge.year, maximumAge.monthNumber - 1, maximumAge.date)
    : dobDate;

  const { relationData, setRelationData, setRelationType, relationType } =
    useRelationContext();

  const defaultValues: FormData = {
    firstName: '',
    lastName: '',
    relationship: '',
    gender: '',
    dateOfBirth: '',
    dateOfMarriage: '',
    selfDeclaration: false,
  };

  const savedValues: FormData = {
    firstName: relationData?.firstName || '',
    lastName: relationData?.lastName || '',
    relationship: relationData?.relationship || '',
    gender: relationData?.gender || '',
    dateOfBirth: relationData?.dateOfBirth || '',
    dateOfMarriage: relationData?.dateOfMarriage || '',
    selfDeclaration: false,
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    control,
    clearErrors,
    reset,
  } = useForm({
    resolver: zodResolver(
      schema(relation, dobDate, marriageDate, maxAgeDate, minAgeDate),
    ),
    defaultValues,
  });

  const { field: relationShipField } = useController({
    control,
    name: 'relationship',
    rules: {
      required: true,
    },
  });
  const { field: genderField } = useController({
    control,
    name: 'gender',
    rules: {
      required: true,
    },
  });
  const { onChange: onRelationshipChange, value: relationshipValue } =
    relationShipField;
  const { onChange: onGenderChange, value: genderValue } = genderField;

  const relationshipTypes =
    relation === EMemberRelationship.SPOUSE
      ? [
          {
            label: 'Husband',
            value: 'Husband',
          },
          {
            label: 'Wife',
            value: 'Wife',
          },
        ]
      : [
          {
            label: 'Son',
            value: 'Son',
          },
          {
            label: 'Daughter',
            value: 'Daughter',
          },
        ];

  const genderTypes = [
    {
      label: 'Male',
      value: 'MALE',
    },
    {
      label: 'Female',
      value: 'FEMALE',
    },
  ];

  useEffect(() => {
    if (relationshipValue === 'Husband' || relationshipValue === 'Son') {
      setValue('gender', 'MALE');
    } else if (
      relationshipValue === 'Wife' ||
      relationshipValue === 'Daughter'
    ) {
      setValue('gender', 'FEMALE');
    }
  }, [relationshipValue, setValue]);

  // Automatically set relationship based on gender
  useEffect(() => {
    if (genderField.value === 'MALE') {
      setValue(
        'relationship',
        relation === EMemberRelationship.SPOUSE ? 'Husband' : 'Son',
      );
    } else if (genderField.value === 'FEMALE') {
      setValue(
        'relationship',
        relation === EMemberRelationship.SPOUSE ? 'Wife' : 'Daughter',
      );
    }
  }, [genderField.value, setValue, relation]);
  // Reset form with saved values when relationData changes and relations match
  useEffect(() => {
    if (relationData && relationType === relation) {
      reset({
        ...savedValues,
        dateOfBirth: format(
          new Date(relationData?.dateOfBirth ?? ''),
          'dd-MMM-yyyy',
        ),
        dateOfMarriage: relationData?.dateOfMarriage
          ? format(new Date(relationData.dateOfMarriage), 'dd-MMM-yyyy')
          : '',
      });
    } else {
      reset(defaultValues);
    }
  }, [relationData, relation, relationType, reset]);

  const watchDateOfBirth = watch('dateOfBirth');

  const dateOfBirthInputValue = watchDateOfBirth
    ? format(new Date(watchDateOfBirth ?? ''), 'dd-MMM-yyyy')
    : '';

  const watchDateOfMarriage = watch('dateOfMarriage');

  const dateOfMarriageInputValue = watchDateOfMarriage
    ? format(new Date(watchDateOfMarriage ?? ''), 'dd-MMM-yyyy')
    : '';

  const navigate = useNavigate();

  const onSubmit = (data: FormData) => {
    if (!watch('selfDeclaration')) {
      toast(
        <div className="flex items-center">
          <InfoIcon fill="#FFF" className="mr-2 w-4 h-4" />
          Please accept the declaration before proceeding
        </div>,
        {
          duration: 4000,
          position: 'bottom-center',
          style: {
            minWidth: isMobile ? '336px' : '420px',
          },
        },
      );
      return;
    }
    setRelationType(relation);
    setRelationData(data);
    navigate('/family-addition/review-details');
  };

  const { isMobile } = useBreakpoints();

  const childDisclaimerText = `As per Insurer guidelines, the date of birth of the child should be within last ${calculateDaysFromToday(
    new Date(
      backDateForDOB?.year,
      backDateForDOB?.monthNumber - 1,
      backDateForDOB?.date,
    ),
  )} days`;

  const spouseDisclaimerText = `As per Insurer guidelines, the date of marriage should be within last ${calculateDaysFromToday(
    new Date(
      backDateForMarriageDate?.year,
      backDateForMarriageDate?.monthNumber - 1,
      backDateForMarriageDate?.date,
    ),
  )} days`;

  const SaveButton = ({ className }: { className: string }) => {
    return (
      <Button className={className} onClick={handleSubmit(onSubmit)}>
        Save
      </Button>
    );
  };
  const content = (
    <div className="flex flex-col items-center justify-center">
      <div
        className={clsx({
          'bg-white p-16 rounded-3xl': true,
          'w-[55%] flex items-center justify-start m-auto mt-12': !isMobile,
          'p-5 h-full': isMobile,
        })}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <p className="text-lg md:text-[32px] text-gray-800 font-semibold">
            Add <span className="lowercase">{relation}</span>
          </p>

          {/* Use grid for layout */}
          <div
            className={clsx(
              'grid grid-cols-1 mt-8',
              isMobile ? 'gap-y-6' : 'md:grid-cols-2 gap-x-8 gap-y-6',
            )}
          >
            {/* First Name */}
            <div>
              <FormInput
                label="First name"
                inputProps={{
                  value: watch('firstName'),
                  required: true,
                  placeholder: 'First name',
                  className:
                    'border-gray-100 focus:outline-none focus:border-gray-500 placeholder:text-gray-400 focus:placeholder:text-gray-200 p-4',
                  ...register('firstName', {
                    onChange: (e) => {
                      const filteredValue = e.target.value.replace(
                        /[^A-Za-z]/g,
                        '',
                      );
                      setValue('firstName', filteredValue);
                      if (errors.firstName) {
                        clearErrors('firstName');
                      }
                    },
                  }),
                }}
                errors={errors}
              />
            </div>

            {/* Last Name */}
            <div>
              <FormInput
                label="Last name"
                inputProps={{
                  value: watch('lastName'),
                  placeholder: 'Last name',
                  className:
                    'focus:outline-none focus:border-gray-500 placeholder:text-gray-400 focus:placeholder:text-gray-200 p-4',
                  ...register('lastName', {
                    onChange: (e) => {
                      setValue('lastName', e.target.value); // Update the value
                      if (errors.lastName) {
                        clearErrors('lastName'); // Clear the error if it exists
                      }
                    },
                  }),
                }}
                errors={errors}
              />
            </div>

            {/* Relationship */}
            <SelectMenu
              onChange={onRelationshipChange}
              value={relationshipValue}
              defaultValue={
                EMemberRelationship.SPOUSE === relation ? 'Wife' : 'Son'
              }
              className="w-full"
            >
              {({
                open,
                value: currrentValue,
              }: {
                open: boolean;
                value: any;
              }) => {
                const selectedLabel =
                  relationshipTypes.find(
                    (option) => option.value === currrentValue,
                  )?.label || '';
                return (
                  <SelectWrapper>
                    <SelectMenuLabel className="block mb-[7px] font-medium text-mobile-gray-500 after:content-['*'] after:text-primary-500">
                      Relationship with you
                    </SelectMenuLabel>
                    <SelectMenuButton
                      trailingIcon={ArrowDown}
                      className={clsx({
                        'py-3 border focus:border-1 border-mobile-gray-300 focus:border-gray-500 focus:outline-none':
                          true,
                        'py-[22px]': selectedLabel.length === 0,
                      })}
                    >
                      <div>{currrentValue}</div>
                    </SelectMenuButton>
                    {errors.relationship?.message &&
                      typeof errors.relationship?.message === 'string' && (
                        <div className="flex gap-1 mt-2">
                          <Exclaimation
                            className="w-6 h-6"
                            fill={get(twConfig, 'theme.colors.primary[600]')}
                            style={{
                              flex: '0 0 auto',
                            }}
                          />
                          <p className="text-sm text-primary-600 leading-[160%]">
                            {errors.relationship?.message}
                          </p>
                        </div>
                      )}
                    <SelectMenuOptions open={open}>
                      {relationshipTypes.map(({ label, value: val }) => (
                        <SelectMenuOption
                          key={relationshipValue}
                          value={val}
                          className="p-0"
                        >
                          {({ active }) => {
                            return (
                              <div
                                className={clsx({
                                  'p-3': true,
                                  'bg-secondary-100': active,
                                })}
                              >
                                {label}
                              </div>
                            );
                          }}
                        </SelectMenuOption>
                      ))}
                    </SelectMenuOptions>
                  </SelectWrapper>
                );
              }}
            </SelectMenu>

            {/* Gender */}
            <SelectMenu
              onChange={onGenderChange}
              value={genderValue}
              defaultValue="Male"
              className="w-full"
            >
              {({
                open,
                value: currrentValue,
              }: {
                open: boolean;
                value: unknown;
              }) => {
                const selectedLabel =
                  genderTypes.find((option) => option.value === currrentValue)
                    ?.label || '';

                return (
                  <SelectWrapper>
                    <SelectMenuLabel className="block mb-[7px] font-medium text-mobile-gray-500 after:content-['*'] after:text-primary-500">
                      Gender
                    </SelectMenuLabel>
                    <SelectMenuButton
                      trailingIcon={ArrowDown}
                      className={clsx({
                        'py-3 border focus:border-1 border-mobile-gray-300 focus:border-gray-500 focus:outline-none':
                          true,
                        'py-[22px]': selectedLabel.length === 0,
                      })}
                    >
                      <div>{selectedLabel}</div>
                    </SelectMenuButton>
                    {errors.gender?.message &&
                      typeof errors.gender?.message === 'string' && (
                        <div className="flex gap-1 mt-2">
                          <Exclaimation
                            className="w-6 h-6"
                            fill={get(twConfig, 'theme.colors.primary[600]')}
                            style={{
                              flex: '0 0 auto',
                            }}
                          />
                          <p className="text-sm text-primary-600 leading-[160%]">
                            {errors.gender?.message}
                          </p>
                        </div>
                      )}
                    <SelectMenuOptions open={open}>
                      {genderTypes.map(({ label, value: val }) => (
                        <SelectMenuOption
                          key={genderValue}
                          value={val}
                          className="p-0"
                        >
                          {({ active }) => {
                            return (
                              <div
                                className={clsx({
                                  'p-3': true,
                                  'bg-secondary-100': active,
                                })}
                              >
                                {label}
                              </div>
                            );
                          }}
                        </SelectMenuOption>
                      ))}
                    </SelectMenuOptions>
                  </SelectWrapper>
                );
              }}
            </SelectMenu>

            {/* Date of Birth */}
            <div>
              <DatePicker
                inputProps={{
                  errors,
                  label: 'Date of birth',
                  inputProps: {
                    required: true,
                    ...register('dateOfBirth', {
                      required: true,
                    }),
                  },
                }}
                placeholder={`Date of Birth`}
                value={dateOfBirthInputValue}
                dayPickerProps={{
                  toYear: new Date().getFullYear() + 1,
                  defaultMonth:
                    relation === EMemberRelationship.SPOUSE
                      ? maximumAge
                        ? new Date(
                            maximumAge.year,
                            maximumAge.monthNumber - 1,
                            maximumAge.date,
                          )
                        : new Date()
                      : backDateForDOB
                        ? new Date(
                            backDateForDOB.year,
                            backDateForDOB.monthNumber - 1,
                            backDateForDOB.date,
                          )
                        : new Date(),

                  required: true,
                  selected: new Date(watchDateOfBirth ?? ''),
                  onSelect: (selectedDate: Date) => {
                    setValue('dateOfBirth', selectedDate?.toISOString());
                    clearErrors('dateOfBirth');
                  },
                  modifiers: {
                    disabled: {
                      before:
                        relation === EMemberRelationship.SPOUSE
                          ? maximumAge
                            ? new Date(
                                maximumAge.year,
                                maximumAge.monthNumber - 1,
                                maximumAge.date + 1,
                              )
                            : new Date()
                          : backDateForDOB
                            ? new Date(
                                backDateForDOB.year,
                                backDateForDOB.monthNumber - 1,
                                backDateForDOB.date + 1,
                              )
                            : new Date(),
                      after:
                        relation === EMemberRelationship.SPOUSE
                          ? minimumAge
                            ? new Date(
                                minimumAge.year,
                                minimumAge.monthNumber - 1,
                                minimumAge.date + 1,
                              )
                            : undefined
                          : new Date(),
                    },
                  },
                }}
                popperOptions={{
                  placement: (viewportWidth ?? 0) < 1024 ? 'top-end' : 'top',
                  strategy: 'fixed',
                }}
              />
            </div>

            {/* Date of Marriage */}
            {relation === EMemberRelationship.SPOUSE && (
              <DatePicker
                inputProps={{
                  errors,
                  label: 'Date of marriage',
                  inputProps: {
                    required: true,
                    ...register('dateOfMarriage', {
                      required: true,
                    }),
                  },
                }}
                placeholder={`Date of Marriage`}
                value={dateOfMarriageInputValue}
                dayPickerProps={{
                  selected: new Date(watchDateOfMarriage ?? ''),
                  toYear: new Date().getFullYear() + 1,
                  onSelect: (selectedDate: Date) => {
                    setValue('dateOfMarriage', selectedDate?.toISOString());
                    clearErrors('dateOfMarriage');
                  },
                  ...{
                    modifiers: {
                      disabled: {
                        before: backDateForMarriageDate
                          ? new Date(
                              backDateForMarriageDate.year,
                              backDateForMarriageDate.monthNumber - 1,
                              backDateForMarriageDate.date + 1,
                            )
                          : undefined,
                        after: new Date(),
                      },
                    },
                  },
                }}
                popperOptions={{
                  placement: (viewportWidth ?? 0) < 1024 ? 'top-end' : 'top',
                  strategy: 'fixed',
                }}
              />
            )}
          </div>
          <p className="mt-8 flex items-start gap-2 text-gray-600 text-sm font-normal">
            <InfoIcon className="min-h-3 min-w-3 mt-[3px]" />
            {relation === EMemberRelationship.SPOUSE
              ? spouseDisclaimerText
              : childDisclaimerText}
          </p>
          {isMobile && (
            <div className="w-full ml-[-16px]">
              <Checkbox
                iconClassName="pt-1"
                containerClassName="!items-start"
                title={
                  relation === EMemberRelationship.SPOUSE
                    ? SPOUSE_DECLARATION_TEXT
                    : CHILD_DECLARATION_TEXT
                }
                selected={watch('selfDeclaration')}
                {...register('selfDeclaration', {
                  required: 'Please complete all declarations to proceed',
                })}
              />
            </div>
          )}
          {isMobile && <SaveButton className="mt-6" />}
        </form>
      </div>
      {!isMobile && (
        <div className="w-1/2">
          <Checkbox
            iconClassName="pt-1"
            containerClassName="!items-start"
            title={
              relation === EMemberRelationship.SPOUSE
                ? SPOUSE_DECLARATION_TEXT
                : CHILD_DECLARATION_TEXT
            }
            selected={watch('selfDeclaration')}
            {...register('selfDeclaration', {
              required: 'Please complete all declarations to proceed',
            })}
          />
        </div>
      )}
    </div>
  );
  return (
    <FullPageModal
      primaryCTA={<SaveButton className="" />}
      isOpen={isOpen}
      onClose={() => onOpenChange(false)}
      isMWeb={isMobile}
      headerText="Add Family Members"
    >
      {content}
    </FullPageModal>
  );
};

export default AddMemberForm;
